import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ModerationOperators, ModerationFlowsModel, ModerationRole } from '../../../models';
import {
    WeezHeader,
    Button,
    ButtonDropdown,
    DropdownItem,
    Icon,
    Card,
    Table,
    DropdownSort,
    TooltipHelp,
    EmptyState,
    ContentCard,
    FilterSetController,
    Filter
} from '@weezevent/nacre';
import { handleSearchQuery } from '../../manager/components/context';
import { listSelection } from '../../../utils/selection';
import LoadingTable from '../../../components/loadingTable';
import OperatorsRow from './operatorsRow';
import OperatorsExportLogsModal from './components/operatorsExportLogsModal';
import ModerationPaginator from '../components/moderationPaginator';
import { useCurrentPageFilters } from '../../../utils/filters';

const spec = {
    multiple: ['flow__in'],
    fields: ['flow__in', 'search'],
    aliases: {},
    date: [],
    form: []
};
export default function OperatorsList({ organizationId, flowId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const [currentPage, setCurrentPage] = React.useState(0);
    const [pageSize] = React.useState(50);
    const defaultFilters = {};

    const [openExportLogsModal, setOpenExportLogsModal] = React.useState(false);
    const [sortType, setSortType] = React.useState('alpha');
    const [filters, setFilters] = useCurrentPageFilters(spec);
    const [selectedOperators, setSelectedOperators] = React.useState([]);

    const [operators, loadingOperators, syncOperators] = ModerationOperators.useApiModel(
        {
            organizationId,
            flowId,
            query: {
                limit: pageSize,
                offset: currentPage * pageSize,
                ...handleSearchQuery(spec, filters, true)
            }
        },
        [filters, organizationId, flowId, currentPage]
    );

    const [moderationFlows, loadingModerationFlows] = ModerationFlowsModel.useApiModel(
        {
            organizationId,
            launch: operators
        },
        [organizationId]
    );

    const [roles, loadingRoles] = ModerationRole.useApiModel(
        {
            organizationId
        },
        [organizationId]
    );

    const totalCount = React.useMemo(() => {
        if (!operators || loadingOperators) {
            return 0;
        }
        return +operators?.__meta?.headers?.get('total-count');
    }, [operators, loadingOperators]);

    const moderationFlowsOptions = React.useMemo(() => {
        if (!moderationFlows || loadingModerationFlows) {
            return [];
        }
        return moderationFlows.map(flow => flow.toOptions());
    }, [moderationFlows, loadingModerationFlows]);

    const sortedOperators = React.useMemo(() => {
        if (!operators || loadingOperators) {
            return [];
        }
        switch (sortType) {
            case 'alpha':
                operators.sort((a, b) => a.first_name.localeCompare(b.first_name));
                break;
            case '-alpha':
                operators.sort((b, a) => a.first_name.localeCompare(b.first_name));
                break;
            default:
                break;
        }

        return operators;
    }, [operators, loadingOperators, sortType]);

    const operatorsIds = React.useMemo(() => {
        return sortedOperators.map(operator => operator.username);
    }, [sortedOperators]);

    const newUri = React.useMemo(() => {
        if (!flowId) {
            return `/ticket/O${organizationId}/moderation/moderation_users/new`;
        }
        return `/ticket/O${organizationId}/moderation/moderation_flows/${flowId}/moderation_users/new`;
    }, [organizationId, flowId]);

    const handleSelect = React.useCallback(
        operator => {
            setSelectedOperators(listSelection(selectedOperators, operatorsIds, operator));
        },
        [selectedOperators, operatorsIds, setSelectedOperators]
    );

    const onChangeSearch = React.useCallback(
        value => {
            let tmpFilters = { ...filters };
            if (value !== '' && value !== null) {
                tmpFilters = { ...tmpFilters, search: value };
            } else {
                delete tmpFilters.search;
            }
            setFilters(tmpFilters);
        },
        [filters]
    );

    return (
        <>
            <WeezHeader
                title={t('ticket.moderation.operators.list.title')}
                rightComponent={[
                    <Button key="addButton" primary icon={<Icon name={'plus'} />} label={t('ticket.moderation.operators.list.cta.add')} onClick={() => history.push(newUri)} />,
                    <ButtonDropdown
                        key="dropdown"
                        trigger={<Button inverted primary icon={<Icon name="chevron-bottom" />} label={t('ticket.moderation.operators.list.cta.actions')} />}
                        items={[<DropdownItem key="export" item={t('ticket.moderation.operators.list.cta.export')} onClick={() => setOpenExportLogsModal(true)} />]}
                    />
                ]}
            >
                {t('ticket.moderation.operators.list.description')}
            </WeezHeader>
            <Card>
                <ContentCard>
                    <FilterSetController
                        total={totalCount || '-'}
                        labelTotal={t('ticket.moderation.operators.list.filter.count')}
                        labelReset={t('ticket.common.action.reset')}
                        labelFilters={t('ticket.common.cta.filters')}
                        onChange={values => {
                            setFilters(values);
                        }}
                        initialValues={filters}
                        defaultValues={defaultFilters}
                        onReset={() => {}}
                        search={{
                            onChange: (_, value) => {
                                onChangeSearch(value);
                            },
                            handleReset: () => onChangeSearch(null),
                            placeholder: t('ticket.common.action.search'),
                            value: filters?.search || ''
                        }}
                    >
                        {!flowId ? (
                            <Filter
                                index="1"
                                labelSelectAll={t('ticket.common.action.select-all')}
                                labelCTA={t('ticket.common.action.apply')}
                                labelName={t('ticket.moderation.operators.list.filter.moderation')}
                                multiple
                                name="flow__in"
                                options={moderationFlowsOptions}
                            />
                        ) : null}
                    </FilterSetController>
                </ContentCard>
                <Table
                    useNew
                    selectable
                    onSelectAll={() => {
                        handleSelect();
                    }}
                >
                    <Table.THead>
                        <Table.Tr active={operatorsIds.length > 0 && operatorsIds.every(id => selectedOperators.includes(id))}>
                            <th>
                                <DropdownSort
                                    label={t('ticket.moderation.operators.list.tableHeader.operator')}
                                    name="alpha"
                                    active={['alpha', `-alpha`].includes(sortType)}
                                    ordering={sortType}
                                    size="mini"
                                    items={[
                                        <DropdownItem.Icons
                                            key={1}
                                            checked={sortType === 'alpha'}
                                            item={t('fromRateCategory', { min: 'A', max: 'Z' })}
                                            onClick={() => {
                                                setSortType('alpha');
                                            }}
                                        />,
                                        <DropdownItem.Icons
                                            key={2}
                                            checked={sortType === '-alpha'}
                                            item={t('fromRateCategory', { min: 'Z', max: 'A' })}
                                            onClick={() => {
                                                setSortType('-alpha');
                                            }}
                                        />
                                    ]}
                                />
                            </th>
                            <th>
                                {t('ticket.moderation.operators.list.tableHeader.moderations')}
                                <TooltipHelp title={t('ticket.moderation.operators.list.tableHeader.helpModerations')} />
                            </th>
                            <th>{t('ticket.moderation.operators.list.tableHeader.roles')}</th>
                            <th>{t('ticket.moderation.operators.list.tableHeader.actions')}</th>
                        </Table.Tr>
                    </Table.THead>
                    {sortedOperators?.length > 0 && (
                        <Table.TBody>
                            {sortedOperators.map((operator, key) => {
                                return (
                                    <OperatorsRow
                                        key={key}
                                        organizationId={organizationId}
                                        flowId={flowId}
                                        operator={operator}
                                        onSelect={handleSelect}
                                        roles={roles}
                                        selected={selectedOperators}
                                        syncOperators={syncOperators}
                                        moderationFlows={moderationFlows}
                                    />
                                );
                            })}
                        </Table.TBody>
                    )}
                </Table>
                <LoadingTable loading={loadingOperators || loadingRoles || loadingModerationFlows} />
                {!loadingOperators && sortedOperators?.length === 0 && (
                    <EmptyState
                        icon={<Icon name="list" color="black" />}
                        title={t('ticket.moderation.operators.list.emptyState.title')}
                        subtitle={t('ticket.moderation.operators.list.emptyState.subtitle')}
                        labelButton={t('ticket.moderation.operators.list.cta.add')}
                    />
                )}
            </Card>

            <ModerationPaginator currentPage={currentPage} setCurrentPage={setCurrentPage} totalCount={totalCount} pageSize={pageSize} />

            {!!moderationFlowsOptions.length && openExportLogsModal && (
                <OperatorsExportLogsModal
                    organizationId={organizationId}
                    openExportLogsModal={openExportLogsModal}
                    setOpenExportLogsModal={setOpenExportLogsModal}
                    moderationFlowsOptions={moderationFlowsOptions}
                />
            )}
        </>
    );
}

OperatorsList.propTypes = {
    organizationId: PropTypes.string.isRequired
};
